// crmExport.js — builds CSV exports of the CRM in the browser, from the
// data the dashboard has already loaded. Nothing is sent to the server.

const nameKey = (s) => String(s || '').trim().toLowerCase();

// Programs are recognised by how their list names start, so new lists
// (like "Si Her DeFi Cohort 2") are included automatically.
export const PROGRAMS = [
  { id: 'siher', label: 'Si Her (all Si Her programs)', match: (k) => k.startsWith('si her') },
  { id: 'grow3dge', label: 'Grow3dge', match: (k) => k.startsWith('grow3dge') },
  { id: 'wellbeing', label: 'Well-Being', match: (k) => /^well[\s-]?being/.test(k) },
];

export function listsForProgram(programId, allLists) {
  const program = PROGRAMS.find((p) => p.id === programId);
  return program ? (allLists || []).filter((name) => program.match(nameKey(name))) : [];
}

function listsColumn(columns) {
  return (columns || []).find((c) => c.id === 'col-lists' || nameKey(c.name) === 'lists');
}

export function rowLists(row, columns) {
  const col = listsColumn(columns);
  const raw = col ? (row?.data || {})[col.id] : '';
  return String(raw || '').split(/[,;\n|]/).map((s) => s.trim()).filter(Boolean);
}

// Members on any of the given lists. With no lists, everyone.
export function rowsForLists(rows, columns, lists) {
  if (!lists || !lists.length) return rows || [];
  const wanted = new Set(lists.map(nameKey));
  return (rows || []).filter((row) => rowLists(row, columns).some((name) => wanted.has(nameKey(name))));
}

// Same rule the dashboard uses: a column limited to certain lists only
// appears when one of those lists is selected. Name, Email and Lists first.
export function columnsForExport(columns, lists) {
  const pool = columns || [];
  const identity = [];
  ['name', 'email', 'lists'].forEach((key) => {
    const col = pool.find((c) => nameKey(c.name) === key || c.id === `col-${key}`);
    if (col && !identity.includes(col)) identity.push(col);
  });
  const selected = (lists || []).map(nameKey);
  const rest = pool.filter((col) => {
    if (identity.includes(col)) return false;
    if (!selected.length) return true;
    const vis = Array.isArray(col.visibleOnLists) ? col.visibleOnLists : [];
    return !vis.length || vis.some((name) => selected.includes(nameKey(name)));
  });
  return identity.concat(rest);
}

// Values from public forms could otherwise run as spreadsheet formulas.
// A plain Telegram handle (@ plus letters, numbers and underscores) can't
// hold a formula, so it's left as it is.
const PLAIN_HANDLE = /^@[A-Za-z0-9_]{1,64}$/;
function safeCell(value) {
  let text = value === undefined || value === null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text) && !PLAIN_HANDLE.test(text)) text = "'" + text;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildCsv(rows, columns) {
  const header = columns.map((c) => safeCell(c.name)).join(',');
  const body = (rows || []).map((row) => columns.map((c) => safeCell((row.data || {})[c.id])).join(','));
  // The byte-order mark makes Excel read accented names correctly.
  return '\uFEFF' + [header, ...body].join('\r\n');
}

export function exportFileName(scopeLabel, date = new Date()) {
  const slug = String(scopeLabel || 'crm').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `si3-crm-${slug}-${date.toISOString().slice(0, 10)}.csv`;
}

export function downloadCsv(csv, fileName) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
